import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IonicModule, ModalController } from '@ionic/angular';
import DOMPurify from 'dompurify';
import MsgReader from '@kenjiuno/msgreader';
import PostalMime, { type Address, type Attachment } from 'postal-mime';
import { firstValueFrom } from 'rxjs';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon';
import { environment } from '@env/environment';

type EmailFileFormat = 'eml' | 'msg';

interface EmailViewerAttachment {
  name: string;
  mediaType: string;
  blob: Blob;
  isInline: boolean;
  isHidden?: boolean;
  contentId?: string;
}

interface EmailViewerModel {
  subject: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  sentAt: string;
  html: SafeHtml | null;
  text: string;
  attachments: EmailViewerAttachment[];
}

@Component({
  selector: 'app-email-viewer',
  templateUrl: './email-viewer.component.html',
  styleUrls: ['./email-viewer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    DynamicIconComponent,
  ],
})
export class EmailViewerComponent implements OnInit, OnDestroy {
  @Input() public fileUrl: string | null = null;
  @Input() public file: File | null = null;
  @Input() public fileName = '';
  @Input() public mediaType = '';
  @Input() public mediaId: string | null = null;

  public isLoading = true;
  public isResizing = false;
  public errorMessage = '';
  public email: EmailViewerModel | null = null;

  private readonly modalController = inject(ModalController);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly httpClient = inject(HttpClient);
  private readonly domSanitizer = inject(DomSanitizer);
  private modalEl?: HTMLIonModalElement;
  public sourceBlob: Blob | null = null;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private hasMoved = false;
  private readonly storageKey = 'emailModalSize';
  private readonly minWidth = 360;
  private readonly minHeight = 280;
  private readonly maxWidthRatio = 0.98;
  private readonly maxHeightRatio = 0.9;
  private boundMove?: (event: MouseEvent) => void;
  private boundUp?: (event: MouseEvent) => void;
  private desiredWidth = 0;
  private desiredHeight = 0;
  private inlineResourceUrls: string[] = [];

  public async ngOnInit(): Promise<void> {
    const saved = this.loadSize();
    const defaultWidth = Math.round(window.innerWidth * 0.82);
    const defaultHeight = Math.round(window.innerHeight * 0.8);
    this.resolveModalEl();
    this.applySize(saved?.width ?? defaultWidth, saved?.height ?? defaultHeight);
    await this.loadEmail();
  }

  public ngOnDestroy(): void {
    this.detachResizeListeners();
    this.revokeInlineResourceUrls();
  }

  public dismiss(): void {
    this.modalController.dismiss();
  }

  public async saveCurrentEmail(): Promise<void> {
    const fallbackName = this.file?.name || this.fileName || 'email';

    try {
      if (this.mediaId) {
        const response = await firstValueFrom(this.httpClient.get(`${environment.apiUrl}/medias/${this.mediaId}/download`, {
          observe: 'response',
          responseType: 'blob',
        }));
        const blob = response.body;
        const originalName = decodeURIComponent(response.headers.get('Original-Name') || fallbackName);
        if (blob) {
          this.triggerDownload(blob, originalName);
        }
        return;
      }

      if (this.fileUrl) {
        const response = await firstValueFrom(this.httpClient.get(this.fileUrl, {
          observe: 'response',
          responseType: 'blob',
        }));
        const blob = response.body;
        const originalName = decodeURIComponent(response.headers.get('Original-Name') || fallbackName);
        if (blob) {
          this.triggerDownload(blob, originalName);
        }
        return;
      }

      if (this.file) {
        this.triggerDownload(this.file, this.file.name || fallbackName);
        return;
      }

      if (this.sourceBlob) {
        this.triggerDownload(this.sourceBlob, fallbackName);
      }
    } catch {
      if (this.sourceBlob) {
        this.triggerDownload(this.sourceBlob, fallbackName);
      }
    }
  }

  public downloadAttachment(attachment: EmailViewerAttachment): void {
    this.triggerDownload(attachment.blob, attachment.name);
  }

  public startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.hasMoved = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.getCurrentWidth();
    this.startHeight = this.getCurrentHeight();
    this.resolveModalEl();
    const rect = this.modalEl?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      this.startWidth = rect.width;
      this.startHeight = rect.height;
    }
    this.attachResizeListeners();
  }

  public onViewerClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }

    const href = anchor.getAttribute('href')?.trim() || '';
    if (!href || href.toLowerCase().startsWith('cid:') || href.startsWith('#')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    window.open(anchor.href, '_blank', 'noopener,noreferrer');
  }

  private async loadEmail(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const blob = await this.resolveSourceBlob();
      this.sourceBlob = blob;
      const arrayBuffer = await blob.arrayBuffer();
      const format = this.detectFileFormat(this.fileName, this.mediaType);

      if (!format) {
        throw new Error('Unsupported email format. Only .eml and .msg files are supported.');
      }

      this.email = format === 'eml'
        ? await this.parseEml(arrayBuffer)
        : this.parseMsg(arrayBuffer);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Unable to open email file.';
    } finally {
      this.isLoading = false;
    }
  }

  private async resolveSourceBlob(): Promise<Blob> {
    if (this.file) {
      this.fileName = this.fileName || this.file.name;
      this.mediaType = this.mediaType || this.file.type || this.getMimeTypeFromName(this.file.name);
      return this.file;
    }

    if (!this.fileUrl) {
      throw new Error('Missing email file source.');
    }

    const response = await firstValueFrom(this.httpClient.get(this.fileUrl, {
      observe: 'response',
      responseType: 'blob',
    }));

    this.fileName = this.fileName || decodeURIComponent(response.headers.get('Original-Name') || 'email');
    this.mediaType = this.mediaType || response.headers.get('Content-Type') || this.getMimeTypeFromName(this.fileName);

    return response.body || new Blob();
  }

  private detectFileFormat(fileName: string, mediaType: string): EmailFileFormat | null {
    const normalizedName = (fileName || '').toLowerCase();
    const normalizedType = (mediaType || '').toLowerCase();

    if (normalizedName.endsWith('.eml') || normalizedType.includes('message/rfc822')) {
      return 'eml';
    }

    if (normalizedName.endsWith('.msg') || normalizedType.includes('vnd.ms-outlook')) {
      return 'msg';
    }

    return null;
  }

  private async parseEml(arrayBuffer: ArrayBuffer): Promise<EmailViewerModel> {
    const parsed = await PostalMime.parse(arrayBuffer, {
      attachmentEncoding: 'arraybuffer',
      maxHeadersSize: 2 * 1024 * 1024,
      maxNestingDepth: 64,
    });

    const referencedContentIds = this.extractReferencedContentIds(parsed.html || '');
    const attachments = parsed.attachments.map((attachment) => this.mapPostalAttachment(attachment, referencedContentIds));
    const html = this.prepareHtmlBody(parsed.html || '', attachments);

    return {
      subject: parsed.subject || this.fileName || '(no subject)',
      from: this.formatAddress(parsed.from),
      to: this.formatAddressList(parsed.to),
      cc: this.formatAddressList(parsed.cc),
      bcc: this.formatAddressList(parsed.bcc),
      sentAt: parsed.date || '',
      html,
      text: parsed.text || '',
      attachments: attachments.filter((attachment) => !attachment.isHidden && !attachment.isInline),
    };
  }

  private parseMsg(arrayBuffer: ArrayBuffer): EmailViewerModel {
    const reader = new MsgReader(arrayBuffer);
    const parsed = reader.getFileData();

    if (parsed.error) {
      throw new Error(parsed.error);
    }

    const htmlSource = parsed.bodyHtml || (parsed.html ? new TextDecoder().decode(parsed.html) : '');
    const referencedContentIds = this.extractReferencedContentIds(htmlSource);

    const attachments = (parsed.attachments || []).map((attachment) => {
      const content = reader.getAttachment(attachment);
      const name = content.fileName || attachment.fileName || attachment.name || 'attachment';
      const type = attachment.attachMimeTag || this.getMimeTypeFromName(name);
      const normalizedContentId = attachment.pidContentId ? this.normalizeContentId(attachment.pidContentId).toLowerCase() : '';
      const isInline = normalizedContentId.length > 0 && referencedContentIds.has(normalizedContentId);
      return {
        name,
        mediaType: type,
        blob: new Blob([this.toArrayBuffer(content.content)], { type }),
        isInline,
        isHidden: attachment.attachmentHidden === true,
        contentId: attachment.pidContentId || undefined,
      } satisfies EmailViewerAttachment;
    });

    return {
      subject: parsed.subject || this.fileName || '(no subject)',
      from: this.formatMailbox(parsed.senderName, parsed.senderEmail),
      to: this.getMsgRecipients(parsed.recipients, 'to'),
      cc: this.getMsgRecipients(parsed.recipients, 'cc'),
      bcc: this.getMsgRecipients(parsed.recipients, 'bcc'),
      sentAt: parsed.messageDeliveryTime || parsed.clientSubmitTime || '',
      html: this.prepareHtmlBody(htmlSource, attachments),
      text: parsed.body || parsed.preview || '',
      attachments: attachments.filter((attachment) => !attachment.isHidden && !attachment.isInline),
    };
  }

  private mapPostalAttachment(attachment: Attachment, referencedContentIds: Set<string>): EmailViewerAttachment {
    const mediaType = attachment.mimeType || this.getMimeTypeFromName(attachment.filename || 'attachment');
    const normalizedContentId = attachment.contentId ? this.normalizeContentId(attachment.contentId).toLowerCase() : '';
    return {
      name: attachment.filename || 'attachment',
      mediaType,
      blob: this.createBlobFromAttachment(attachment.content, mediaType, attachment.encoding),
      isInline: referencedContentIds.has(normalizedContentId) || (attachment.related === true && this.isImageMediaType(mediaType)),
      contentId: attachment.contentId || undefined,
    };
  }

  private extractReferencedContentIds(html: string): Set<string> {
    const contentIds = new Set<string>();
    if (!html) {
      return contentIds;
    }

    const cidPattern = /cid:([^'"\s>]+)/gi;
    let match: RegExpExecArray | null;
    while ((match = cidPattern.exec(html)) !== null) {
      const value = this.normalizeContentId(match[1]).toLowerCase();
      if (value) {
        contentIds.add(value);
      }
    }

    return contentIds;
  }

  private createBlobFromAttachment(content: ArrayBuffer | Uint8Array | string, mediaType: string, encoding?: 'base64' | 'utf8'): Blob {
    if (content instanceof ArrayBuffer) {
      return new Blob([content], { type: mediaType });
    }

    if (content instanceof Uint8Array) {
      return new Blob([this.toArrayBuffer(content)], { type: mediaType });
    }

    if (encoding === 'base64') {
      const binary = atob(content);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }
      return new Blob([bytes], { type: mediaType });
    }

    return new Blob([content], { type: mediaType || 'text/plain;charset=utf-8' });
  }

  private prepareHtmlBody(rawHtml: string, attachments: EmailViewerAttachment[]): SafeHtml | null {
    if (!rawHtml) {
      return null;
    }

    this.revokeInlineResourceUrls();

    if (typeof DOMParser === 'undefined') {
      return this.trustSanitizedHtml(rawHtml);
    }

    const parser = new DOMParser();
    const documentNode = parser.parseFromString(rawHtml, 'text/html');
    const inlineAttachments = new Map<string, { attachment: EmailViewerAttachment; objectUrl: string }>();

    for (const attachment of attachments) {
      if (!attachment.isInline || !attachment.contentId) {
        continue;
      }

      const objectUrl = URL.createObjectURL(attachment.blob);
      this.inlineResourceUrls.push(objectUrl);
      inlineAttachments.set(this.normalizeContentId(attachment.contentId).toLowerCase(), {
        attachment,
        objectUrl,
      });
    }

    const referencedElements = documentNode.querySelectorAll<HTMLElement>('[src], [href], [data]');
    for (const element of Array.from(referencedElements)) {
      this.replaceInlineReference(element, 'src', inlineAttachments, documentNode);
      this.replaceInlineReference(element, 'href', inlineAttachments, documentNode);
      this.replaceInlineReference(element, 'data', inlineAttachments, documentNode);
    }

    this.decorateExternalLinks(documentNode);

    this.replaceUnsupportedInlineImages(documentNode);

    return this.trustSanitizedHtml(documentNode.body.innerHTML);
  }

  private trustSanitizedHtml(html: string): SafeHtml {
    const sanitizedHtml = DOMPurify.sanitize(html || '', {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
      FORBID_ATTR: ['formaction'],
    });

    return this.domSanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  private replaceInlineReference(
    element: HTMLElement,
    attributeName: 'src' | 'href' | 'data',
    inlineAttachments: Map<string, { attachment: EmailViewerAttachment; objectUrl: string }>,
    documentNode: Document,
  ): void {
    const attributeValue = element.getAttribute(attributeName)?.trim();
    if (!attributeValue || !attributeValue.toLowerCase().startsWith('cid:')) {
      return;
    }

    const contentId = this.normalizeContentId(attributeValue.slice(4)).toLowerCase();
    const inlineAttachment = inlineAttachments.get(contentId);
    if (!inlineAttachment) {
      return;
    }

    if (!this.isImageMediaType(inlineAttachment.attachment.mediaType) && this.shouldReplaceWithAttachmentPlaceholder(element, attributeName)) {
      const placeholder = this.createInlineAttachmentPlaceholder(documentNode, inlineAttachment.attachment, inlineAttachment.objectUrl);
      element.replaceWith(placeholder);
      return;
    }

    element.setAttribute(attributeName, inlineAttachment.objectUrl);
    if (attributeName === 'href') {
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener noreferrer');
    }
  }

  private replaceUnsupportedInlineImages(documentNode: Document): void {
    const imageElements = documentNode.querySelectorAll<HTMLImageElement>('img');
    for (const imageElement of Array.from(imageElements)) {
      const src = imageElement.getAttribute('src')?.trim() || '';
      if (!src) {
        imageElement.replaceWith(this.createUnknownInlinePlaceholder(documentNode));
        continue;
      }

      if (src.toLowerCase().startsWith('cid:')) {
        imageElement.replaceWith(this.createUnknownInlinePlaceholder(documentNode));
        continue;
      }

      if (src.toLowerCase().startsWith('data:') && !this.isImageDataUri(src)) {
        imageElement.replaceWith(this.createUnknownInlinePlaceholder(documentNode));
      }
    }
  }

  private decorateExternalLinks(documentNode: Document): void {
    const anchors = documentNode.querySelectorAll<HTMLAnchorElement>('a[href]');
    for (const anchor of Array.from(anchors)) {
      const href = anchor.getAttribute('href')?.trim() || '';
      if (!href || href.toLowerCase().startsWith('cid:') || href.startsWith('#')) {
        continue;
      }

      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  }

  private shouldReplaceWithAttachmentPlaceholder(element: HTMLElement, attributeName: 'src' | 'href' | 'data'): boolean {
    if (attributeName === 'href') {
      return false;
    }

    const tagName = element.tagName.toLowerCase();
    return tagName === 'img' || tagName === 'object' || tagName === 'embed' || tagName === 'iframe';
  }

  private createInlineAttachmentPlaceholder(documentNode: Document, attachment: EmailViewerAttachment, objectUrl: string): HTMLElement {
    const wrapper = documentNode.createElement('a');
    wrapper.className = 'email-inline-attachment';
    wrapper.href = objectUrl;
    wrapper.target = '_blank';
    wrapper.rel = 'noopener noreferrer';

    const label = documentNode.createElement('span');
    label.className = 'email-inline-attachment__label';
    label.textContent = 'Inline attachment';

    const fileName = documentNode.createElement('span');
    fileName.className = 'email-inline-attachment__name';
    fileName.textContent = attachment.name;

    wrapper.append(label, fileName);
    return wrapper;
  }

  private createUnknownInlinePlaceholder(documentNode: Document): HTMLElement {
    const wrapper = documentNode.createElement('span');
    wrapper.className = 'email-inline-attachment email-inline-attachment--missing';

    const label = documentNode.createElement('span');
    label.className = 'email-inline-attachment__label';
    label.textContent = 'Inline content unavailable';

    wrapper.append(label);
    return wrapper;
  }

  private isImageMediaType(mediaType: string): boolean {
    return (mediaType || '').toLowerCase().startsWith('image/');
  }

  private isImageDataUri(value: string): boolean {
    return /^data:image\//i.test(value);
  }

  private formatAddress(address?: Address): string {
    if (!address) {
      return '';
    }

    if ('group' in address && Array.isArray(address.group)) {
      const members = address.group.map((entry) => this.formatMailbox(entry.name, entry.address)).join(', ');
      return address.name ? `${address.name}: ${members}` : members;
    }

    return this.formatMailbox(address.name, address.address);
  }

  private formatAddressList(addresses?: Address[]): string[] {
    return (addresses || [])
      .map((address) => this.formatAddress(address))
      .filter((value) => value.length > 0);
  }

  private formatMailbox(name?: string, address?: string): string {
    const trimmedName = (name || '').trim();
    const trimmedAddress = (address || '').trim();

    if (trimmedName && trimmedAddress) {
      return `${trimmedName} <${trimmedAddress}>`;
    }

    return trimmedAddress || trimmedName;
  }

  private getMsgRecipients(
    recipients: Array<{ name?: string; email?: string; recipType?: string }> | undefined,
    recipientType: 'to' | 'cc' | 'bcc',
  ): string[] {
    return (recipients || [])
      .filter((recipient) => recipient.recipType === recipientType)
      .map((recipient) => this.formatMailbox(recipient.name, recipient.email))
      .filter((value) => value.length > 0);
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName || 'download';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
  }

  private normalizeContentId(contentId: string): string {
    return contentId.replace(/^<|>$/g, '');
  }

  private getMimeTypeFromName(fileName: string): string {
    const lowerName = (fileName || '').toLowerCase();
    if (lowerName.endsWith('.eml')) {
      return 'message/rfc822';
    }
    if (lowerName.endsWith('.msg')) {
      return 'application/vnd.ms-outlook';
    }
    if (lowerName.endsWith('.pdf')) {
      return 'application/pdf';
    }
    if (lowerName.endsWith('.html') || lowerName.endsWith('.htm')) {
      return 'text/html';
    }
    if (lowerName.endsWith('.txt')) {
      return 'text/plain';
    }
    if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) {
      return 'image/jpeg';
    }
    if (lowerName.endsWith('.png')) {
      return 'image/png';
    }
    if (lowerName.endsWith('.gif')) {
      return 'image/gif';
    }
    return 'application/octet-stream';
  }

  private toArrayBuffer(content: Uint8Array): ArrayBuffer {
    return content.buffer.slice(content.byteOffset, content.byteOffset + content.byteLength) as ArrayBuffer;
  }

  private revokeInlineResourceUrls(): void {
    for (const url of this.inlineResourceUrls) {
      URL.revokeObjectURL(url);
    }
    this.inlineResourceUrls = [];
  }

  private attachResizeListeners(): void {
    this.boundMove = (evt: MouseEvent) => this.onResizeMove(evt);
    this.boundUp = () => this.stopResize();
    document.addEventListener('mousemove', this.boundMove);
    document.addEventListener('mouseup', this.boundUp);
  }

  private detachResizeListeners(): void {
    if (this.boundMove) {
      document.removeEventListener('mousemove', this.boundMove);
    }
    if (this.boundUp) {
      document.removeEventListener('mouseup', this.boundUp);
    }
    this.boundMove = undefined;
    this.boundUp = undefined;
  }

  private onResizeMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    if (Math.abs(deltaX) + Math.abs(deltaY) < 2) {
      return;
    }
    this.hasMoved = true;
    this.applySize(this.startWidth + deltaX, this.startHeight + deltaY);
  }

  private stopResize(): void {
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
    this.detachResizeListeners();
    if (this.hasMoved) {
      this.saveSize();
    }
  }

  private applySize(width: number, height: number): void {
    this.desiredWidth = width;
    this.desiredHeight = height;
    const maxWidth = Math.floor(window.innerWidth * this.maxWidthRatio);
    const maxHeight = Math.floor(window.innerHeight * this.maxHeightRatio);
    const clampedWidth = Math.min(Math.max(width, this.minWidth), maxWidth);
    const clampedHeight = Math.min(Math.max(height, this.minHeight), maxHeight);
    if (this.modalEl) {
      this.modalEl.style.setProperty('--width', `${clampedWidth}px`);
      this.modalEl.style.setProperty('--height', `${clampedHeight}px`);
      this.modalEl.style.setProperty('--min-width', `${this.minWidth}px`);
      this.modalEl.style.setProperty('--min-height', `${this.minHeight}px`);
    }
  }

  private getCurrentWidth(): number {
    const current = this.modalEl?.style.getPropertyValue('--width') || '';
    const parsed = parseInt(current || '0', 10);
    return Number.isNaN(parsed) ? Math.round(window.innerWidth * 0.82) : parsed;
  }

  private getCurrentHeight(): number {
    const current = this.modalEl?.style.getPropertyValue('--height') || '';
    const parsed = parseInt(current || '0', 10);
    return Number.isNaN(parsed) ? Math.round(window.innerHeight * 0.8) : parsed;
  }

  private saveSize(): void {
    const payload = {
      width: this.getCurrentWidth(),
      height: this.getCurrentHeight(),
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch {
      return;
    }
  }

  private loadSize(): { width: number; height: number } | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (!parsed.width || !parsed.height) {
        return null;
      }
      return { width: parsed.width, height: parsed.height };
    } catch {
      return null;
    }
  }

  private resolveModalEl(): void {
    if (this.modalEl) {
      return;
    }
    const host = this.hostRef.nativeElement;
    const modal = host.closest('ion-modal') as HTMLIonModalElement | null;
    if (modal) {
      this.modalEl = modal;
      if (this.desiredWidth && this.desiredHeight) {
        this.applySize(this.desiredWidth, this.desiredHeight);
      }
      return;
    }
    this.modalController.getTop().then((top) => {
      this.modalEl = top || undefined;
      if (this.modalEl && this.desiredWidth && this.desiredHeight) {
        this.applySize(this.desiredWidth, this.desiredHeight);
      }
    });
  }
}